/**
 * Format a number as currency
 * @param amount - The amount to format
 * @param currency - The currency code (default: 'USD')
 * @returns Formatted currency string or '-' if amount is null, undefined, or NaN
 */
function formatCurrency(amount: number | null | undefined, currency = 'USD'): string {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return '-';
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * Format a number as percentage
 * @param value - The value to format (e.g., 0.1 for 10% or use as number 10)
 * @param decimals - Number of decimal places (default: 1)
 * @param isRatio - If true, treats value as ratio (0.1 = 10%), if false treats as number (10 = 10%)
 * @returns Formatted percentage string
 */
function formatPercentage(value: number, decimals = 1, isRatio = false): string {
  if (isRatio) {
    return new Intl.NumberFormat('en-US', {
      style: 'percent',
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(value);
  }
  return `${value.toFixed(decimals)}%`;
}

/**
 * Format a number with locale-specific formatting (no abbreviations)
 * @param num - The number to format
 * @param locale - The locale string (default: 'en-US')
 * @returns Formatted number string with locale-specific formatting
 * @example
 * formatNumber(1234) // "1,234"
 * formatNumber(1234567) // "1,234,567"
 */
function formatNumber(num: number, locale = 'en-US'): string {
  return new Intl.NumberFormat(locale).format(num);
}

/**
 * Format a large number with abbreviations (K, M, B)
 * @param num - The number to format
 * @returns Formatted number string with abbreviation
 */
function formatCompactNumber(num: number): string {
  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    compactDisplay: 'short',
  }).format(num);
}

/**
 * Format large numbers with K/M/B abbreviations (alternative implementation)
 * @param value - The number to format
 * @returns Formatted number string with abbreviation
 */
function formatNumberWithAbbr(value: number): string {
  if (value >= 1000000000) {
    return `${(value / 1000000000).toFixed(1)}B`;
  }
  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}K`;
  }
  return value.toString();
}

// Namespace export for convenient usage: numberUtil.formatCurrency()
export const numberUtil = {
  formatCurrency,
  formatPercentage,
  formatNumber,
  formatCompactNumber,
  formatNumberWithAbbr,
} as const;

